import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { History, Download, Loader2, Pencil, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import {
  adminMe,
  setAdminToken,
  getAdminToken,
  invoiceGenListRecords,
  invoiceGenDownloadRecord,
  invoiceGenSendEmail,
} from "@/lib/api";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function InvoiceHistory() {
  const nav = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (!getAdminToken()) { nav("/admin/login", { replace: true }); return; }
    adminMe()
      .then((res) => { if (res?.expires_at) setAdminToken(getAdminToken(), res.expires_at); })
      .catch(() => { setAdminToken(""); nav("/admin/login", { replace: true }); });
  }, [nav]);

  useSessionRenewal({
    onExpired: () => { setAdminToken(""); nav("/admin/login", { replace: true }); },
  });

  useEffect(() => {
    invoiceGenListRecords()
      .then((res) => setRecords(res.records || []))
      .catch(() => toast.error("Could not load invoice history."))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async (record) => {
    setDownloadingId(record.id);
    try {
      const blob = await invoiceGenDownloadRecord(record.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ASHLAN_Invoice_${(record.invoice_number || "invoice").replace(/[^A-Za-z0-9-]/g, "")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not re-generate this PDF.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleEdit = (record) => {
    nav("/admin/invoices", { state: { editRecordId: record.id } });
  };

  const openSendDialog = (record) => {
    setEmailTarget(record);
    setEmailInput(record.bill_to_email || "");
  };

  const handleConfirmSend = async () => {
    if (!emailTarget) return;
    const recipient = emailInput.trim();
    if (!recipient || !recipient.includes("@")) {
      toast.error("Enter a valid recipient email.");
      return;
    }
    setSendingEmail(true);
    try {
      const alreadySent = emailTarget.workflow_status === "sent";
      const res = await invoiceGenSendEmail(emailTarget.id, { recipientEmail: recipient, force: alreadySent });
      toast.success(`Invoice ${res.invoice_number} emailed to ${res.recipient}.`);
      setRecords((rs) =>
        rs.map((r) =>
          r.id === emailTarget.id
            ? { ...r, workflow_status: "sent", email_recipient: res.recipient, email_sent_at: res.sent_at }
            : r
        )
      );
      setEmailTarget(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the email.");
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-10" data-testid="admin-invoice-history-page">
      <SEO title="Invoice History" noIndex />
      <div className="flex items-center gap-3 mb-2">
        <History className="text-[#3A7A8C]" size={22} />
        <h1 className="text-2xl font-bold text-[#080808]">Invoice History</h1>
      </div>
      <p className="text-sm text-[#6B7280] mb-8">
        Every invoice generated through the{" "}
        <Link to="/admin/invoices" className="underline hover:text-[#3A7A8C]">
          Invoice Generator
        </Link>
        . PDFs are re-created on demand from the saved record.
      </p>

      {loading ? (
        <Loader2 className="animate-spin text-[#3A7A8C]" size={22} />
      ) : records.length === 0 ? (
        <p className="text-sm text-[#9CA3AF]" data-testid="invoice-history-empty">
          No invoices generated yet.
        </p>
      ) : (
        <div className="overflow-x-auto border border-[#E5E7EB] rounded-lg">
          <table className="w-full text-sm" data-testid="invoice-history-table">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Invoice #</th>
                <th className="text-left font-semibold px-4 py-3">Client</th>
                <th className="text-left font-semibold px-4 py-3">Invoice Date</th>
                <th className="text-left font-semibold px-4 py-3">Total</th>
                <th className="text-left font-semibold px-4 py-3">Generated At</th>
                <th className="text-left font-semibold px-4 py-3">Email Status</th>
                <th className="text-left font-semibold px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id} className="border-b border-[#F3F4F6]" data-testid={`invoice-history-row-${r.invoice_number}`}>
                  <td className="px-4 py-3 font-semibold">{r.invoice_number}</td>
                  <td className="px-4 py-3">{r.bill_to_name || "—"}</td>
                  <td className="px-4 py-3">{r.invoice_date || "—"}</td>
                  <td className="px-4 py-3">${(r.totals?.total ?? 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-[#6B7280]">{new Date(r.generated_at).toLocaleString()}</td>
                  <td className="px-4 py-3" data-testid={`invoice-history-email-status-${r.invoice_number}`}>
                    {r.workflow_status === "sent" ? (
                      <div className="flex flex-col gap-1">
                        <span
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-[#166534] bg-[#F0FDF4] border border-[#DCFCE7] rounded-full px-2.5 py-1 w-fit"
                          title={`Sent to ${r.email_recipient || "—"}`}
                        >
                          <CheckCircle2 size={12} /> Sent
                        </span>
                        {r.email_sent_at && (
                          <span
                            className="text-[11px] text-[#6B7280]"
                            data-testid={`invoice-history-email-sent-at-${r.invoice_number}`}
                          >
                            {new Date(r.email_sent_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#6B7280] bg-[#F9FAFB] border border-[#E5E7EB] rounded-full px-2.5 py-1">
                        Not sent
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => handleEdit(r)}
                        data-testid={`invoice-history-edit-${r.invoice_number}`}
                        className="inline-flex items-center gap-1.5 text-[#3A7A8C] hover:text-[#080808]"
                      >
                        <Pencil size={14} /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownload(r)}
                        disabled={downloadingId === r.id}
                        data-testid={`invoice-history-download-${r.invoice_number}`}
                        className="inline-flex items-center gap-1.5 text-[#3A7A8C] hover:text-[#080808] disabled:opacity-60"
                      >
                        {downloadingId === r.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                        Download
                      </button>
                      <button
                        type="button"
                        onClick={() => openSendDialog(r)}
                        data-testid={`invoice-history-send-email-${r.invoice_number}`}
                        className="inline-flex items-center gap-1.5 text-[#3A7A8C] hover:text-[#080808]"
                      >
                        <Mail size={14} /> {r.workflow_status === "sent" ? "Resend" : "Send Email"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!emailTarget} onOpenChange={(open) => !open && setEmailTarget(null)}>
        <DialogContent data-testid="invoice-history-send-dialog">
          <DialogHeader>
            <DialogTitle>
              {emailTarget?.workflow_status === "sent" ? "Resend invoice" : "Send invoice"}
              {emailTarget ? ` ${emailTarget.invoice_number}` : ""}
            </DialogTitle>
            <DialogDescription>
              {emailTarget?.workflow_status === "sent"
                ? `This invoice was already sent to ${emailTarget.email_recipient || "—"}${
                    emailTarget.email_sent_at ? ` on ${new Date(emailTarget.email_sent_at).toLocaleString()}` : ""
                  }. Sending again will email it a second time.`
                : "This will email the invoice PDF with the standard Ashlan Clinic message."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="send-email-recipient">Recipient email</Label>
            <Input
              id="send-email-recipient"
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="client@example.com"
              data-testid="invoice-history-send-dialog-email-input"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={handleConfirmSend}
              disabled={sendingEmail}
              data-testid="invoice-history-send-dialog-confirm"
              className="ash-btn-primary disabled:opacity-60"
            >
              {sendingEmail ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              {emailTarget?.workflow_status === "sent" ? "Send Again" : "Send Email"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
